import { createTheme } from '@mui/material/styles';
import '@mui/material/Card';

const colors = {
  brand: {
    yellow: '#FFCD00',
    yellowHover: '#F2C400',
    primary: '#316A7D',
    primaryHover: 'rgba(49, 106, 125, 0.08)',
  },

  neutral: {
    dark: '#1D2632',
    medium: '#4A515B',
    light: '#777D84',
    lighter: '#D2D4D6',
    lightest: '#F4F7F9',
    white: '#FFFFFF',
  },

  ui: {
    border: '#C5DBE3',
    borderHover: '#bbb',
    borderFocus: '#1976d2',
    background: '#e2edf1',
    accent: '#6DA6B9',
    error: '#d32f2f',
    success: '#2e7d32',
    warning: '#ed6c02',
    info: '#0288d1',
  },

  tags: {
    lightTagBackground: '#FDF4C8',
    lightTagText: '#875500',
    darkTagBackground: '#CDF0EA',
    darkTagText: '#005C3E',
  },

  searchFilter: {
    filterOptionBackground: '#D3E4EA',
    filterOptionText: '#555',
  },

  menu: {
    background: '#EFF2F7',
    itemHover: '#E4EAF1',
  },

  component: {
    cardBackground: '#FFFFFF',
    cardBorder: '#e0e0e0',
    shadowLight: 'rgba(0, 0, 0, 0.1)',
    hoverOverlay: 'rgba(0, 0, 0, 0.04)',
  },

  code: {
    text: '#2d3748',
    background: '#FEF9E4',
  },
};

const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
  xxxl: 48,
  xxxxl: 64,
};

export const toPixels = (value: number): string => `${value}px`;

export const getSpacing = {
  xs: toPixels(spacing.xs),
  sm: toPixels(spacing.sm),
  md: toPixels(spacing.md),
  lg: toPixels(spacing.lg),
  xl: toPixels(spacing.xl),
  xxl: toPixels(spacing.xxl),
  xxxl: toPixels(spacing.xxxl),
  xxxxl: toPixels(spacing.xxxxl),
};

export const spacingToPx = (spacingKey: keyof typeof spacing): string => {
  return toPixels(spacing[spacingKey]);
};

const borderRadius = {
  none: 0,
  sm: 4,
  md: 5,
  lg: 8,
  xl: 12,
  xxl: 16,
  round: '50%',
  pill: 48,
};

const fontWeights = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

const apiComponentStyles = {
  tag: {
    paddingLeft: spacing.sm,
    paddingRight: spacing.sm,
    paddingTop: spacing.xs,
    paddingBottom: spacing.xs,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.sm,
    display: 'flex',
  },
  flexContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  flexContainerWithGap: (gap: number) => ({
    display: 'flex',
    alignItems: 'center',
    gap,
  }),
};

declare module '@mui/material/styles' {
  interface Palette {
    surface: {
      neutralDark: string;
      white: string;
      navItemInactive: string;
    };
    brand: {
      yellow: string;
      primary: string;
      primaryHover: string;
    };
    neutral: {
      dark: string;
      medium: string;
      light: string;
      lighter: string;
      lightest: string;
      white: string;
    };
    ui: {
      border: string;
      borderHover: string;
      borderFocus: string;
      background: string;
      accent: string;
      error: string;
      success: string;
      warning: string;
      info: string;
    };
    tags: {
      lightTagBackground: string;
      lightTagText: string;
      darkTagBackground: string;
      darkTagText: string;
    };
    searchFilter: {
      filterOptionBackground: string;
      filterOptionText: string;
    };
    menu: {
      background: string;
      itemHover: string;
    };
    component: {
      cardBackground: string;
      cardBorder: string;
      shadowLight: string;
      hoverOverlay: string;
    };
    code: {
      text: string;
      background: string;
    };
  }
  interface PaletteOptions {
    surface?: {
      neutralDark?: string;
      white?: string;
      navItemInactive?: string;
    };
    brand?: {
      yellow?: string;
      primary?: string;
      primaryHover?: string;
    };
    neutral?: {
      dark?: string;
      medium?: string;
      light?: string;
      lighter?: string;
      lightest?: string;
      white?: string;
    };
    ui?: {
      border?: string;
      borderHover?: string;
      borderFocus?: string;
      background?: string;
      accent?: string;
      error?: string;
      success?: string;
      warning?: string;
      info?: string;
    };
    tags?: {
      lightTagBackground: string;
      lightTagText: string;
    };
    searchFilter: {
      filterOptionBackground: string;
      filterOptionText: string;
    };
    menu?: {
      background?: string;
      itemHover?: string;
    };
    component?: {
      cardBackground?: string;
      cardBorder?: string;
      shadowLight?: string;
      hoverOverlay?: string;
    };
    code?: {
      text?: string;
      background?: string;
    };
  }
  interface Theme {
    customSpacing: typeof spacing;
    customBorderRadius: typeof borderRadius;
    customFontWeights: typeof fontWeights;
    apiStyles: typeof apiComponentStyles;
  }
  interface ThemeOptions {
    customSpacing?: typeof spacing;
    customBorderRadius?: typeof borderRadius;
    customFontWeights?: typeof fontWeights;
    apiStyles?: typeof apiComponentStyles;
  }
}

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    apiCard: true;
    apiHeader: true;
  }
}

export const theme = createTheme({
  customSpacing: spacing,
  customBorderRadius: borderRadius,
  customFontWeights: fontWeights,
  apiStyles: apiComponentStyles,
  palette: {
    mode: 'light',
    primary: {
      main: colors.brand.yellow,
      contrastText: colors.neutral.dark,
    },
    background: {
      default: colors.neutral.lightest,
      paper: colors.neutral.dark,
    },
    text: {
      primary: colors.neutral.dark,
      secondary: colors.neutral.medium,
    },
    divider: colors.ui.border,
    grey: {
      500: colors.neutral.light,
    },
    surface: {
      neutralDark: colors.neutral.dark,
      white: colors.neutral.white,
      navItemInactive: colors.neutral.lighter,
    },
    brand: {
      yellow: colors.brand.yellow,
      primary: colors.brand.primary,
    },
    neutral: colors.neutral,
    ui: colors.ui,
    tags: colors.tags,
    searchFilter: colors.searchFilter,
    menu: colors.menu,
    component: colors.component,
    code: colors.code,
  },
  typography: {
    fontFamily:
      'Metropolis UI, Inter, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
    h1: {
      fontSize: 45,
      fontWeight: fontWeights.medium,
      lineHeight: '52px',
      marginBottom: spacing.lg,
    },
    h4: {
      fontSize: 28,
      fontWeight: fontWeights.semibold,
      lineHeight: '36px',
    },
    h5: {
      fontSize: 22,
      fontWeight: fontWeights.semibold,
      lineHeight: '28px',
    },
    h6: {
      fontSize: 14,
      fontWeight: fontWeights.semibold,
      lineHeight: '28px',
    },
    body1: {
      fontWeight: fontWeights.medium,
      lineHeight: '24px',
      fontSize: '16px',
    },
    body2: {
      fontSize: 14,
      fontWeight: fontWeights.medium,
      lineHeight: '20px',
    },
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          height: 46,
          borderRadius: borderRadius.md,
          '& .MuiOutlinedInput-input': {
            padding: `${spacing.md + 1}px ${spacing.lg}px`,
            '::placeholder': { color: colors.neutral.light, opacity: 1 },
          },
          '& fieldset': {
            borderColor: colors.ui.border,
            borderRadius: borderRadius.md,
          },
          '&:hover fieldset': {
            borderColor: colors.ui.borderHover,
          },
          '&.Mui-focused fieldset': {
            borderColor: colors.ui.borderFocus,
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        fullWidth: true,
        size: 'medium',
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        size: 'large',
      },
      styleOverrides: {
        root: {
          borderRadius: borderRadius.pill,
          textTransform: 'none',
        },
        containedPrimary: {
          backgroundColor: colors.brand.yellow,
          color: colors.neutral.dark,
          '&:hover': {
            backgroundColor: colors.brand.yellowHover,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: colors.brand.primary,
          },
          '&.Mui-checked:hover': {
            backgroundColor: colors.brand.primaryHover,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: colors.ui.border,
          opacity: 1,
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: `${spacing.xxl}px !important`,
          height: `${spacing.xxl}px`,
          flexDirection: 'row-reverse',
          '& .MuiAccordionSummary-content': {
            margin: '0 !important',
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            marginRight: spacing.sm,
            marginLeft: 0,
          },
        },
      },
    },
    MuiCard: {
      variants: [
        {
          props: { variant: 'apiCard' },
          style: {
            backgroundColor: colors.component.cardBackground,
            borderRadius: 25,
            boxShadow: `0 2px 4px ${colors.component.shadowLight}`,
            height: '100%',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3,
            },
            border: `1px solid ${colors.component.cardBorder}`,
          },
        },
      ],
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          '&:last-child': {
            paddingBottom: spacing.lg,
          },
        },
      },
    },
    MuiPaper: {
      variants: [
        {
          props: { variant: 'apiHeader' },
          style: {
            width: '100%',
            height: '100%',
            paddingTop: spacing.xl,
            paddingLeft: spacing.xxxl - spacing.sm,
            paddingRight: spacing.xl,
            borderRadius: 16,
            justifyContent: 'space-between',
            alignItems: 'center',
            display: 'inline-flex',
          },
        },
      ],
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 0,
        },
        indicator: {
          height: 2,
        },
      },
    },
    MuiTab: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          minHeight: 0,
          textTransform: 'none',
          paddingTop: spacing.md,
          paddingBottom: spacing.lg,
          paddingLeft: spacing.xl,
          paddingRight: spacing.xl,
          fontWeight: fontWeights.semibold,
          fontSize: 14,
          lineHeight: '20px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
        label: {
          paddingLeft: 0,
          paddingRight: 0,
          fontWeight: fontWeights.semibold,
          fontSize: 10,
          lineHeight: '16px',
        },
      },
    },
  },
});
